import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import UserIcon from 'src/components/ui/UserIcon'
import { getAvatarSx } from 'src/components/common/personaAvatar'
import { usePresenceAction, usePresenceSelect } from 'src/hooks/usePresence'
import { ITeamMemberPresence } from 'src/services/auth/users'
import UserStatusBadge from './UserStatusBadge'
import { useLocationLabel } from './useLocationLabel'

const MAX_AVATARS = 4

const memberName = (member: ITeamMemberPresence) =>
  `${member.info?.firstName ?? ''} ${member.info?.lastName ?? ''}`.trim() ||
  member.identifier ||
  `#${member.id}`

const MemberAvatar = ({
  member,
  size,
}: {
  member: ITeamMemberPresence
  size: number
}) => {
  const name = memberName(member)
  const avatar =
    typeof member.info?.avatar === 'string' ? member.info.avatar : undefined

  return (
    <Avatar
      alt={name}
      src={avatar}
      sx={getAvatarSx(size, Boolean(avatar), Math.round(size * 0.4))}
    >
      {name.slice(0, 1).toUpperCase()}
    </Avatar>
  )
}

const MemberRow = ({
  member,
  onNavigate,
}: {
  member: ITeamMemberPresence
  onNavigate: () => void
}) => {
  const { t } = useTranslation('presence')
  const location = useLocationLabel(member.currentLocation)

  return (
    <Stack direction="row" spacing={3} alignItems="center" sx={{ py: 1.5 }}>
      <UserStatusBadge status={member.status}>
        <MemberAvatar member={member} size={36} />
      </UserStatusBadge>

      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography noWrap fontWeight={500} fontSize={14} color="grey.900">
            {memberName(member)}
          </Typography>
          {member.mobile && (
            <Tooltip title={t('team.mobile')}>
              <span style={{ display: 'inline-flex' }}>
                <UserIcon icon="tabler:device-mobile" fontSize={14} />
              </span>
            </Tooltip>
          )}
        </Stack>

        <Typography noWrap fontSize={12} color="text.secondary">
          {t(`status.${member.status}`)}
          {location && ' · '}
          {location &&
            (location.path ? (
              <Link
                to={location.path}
                onClick={onNavigate}
                style={{ color: 'inherit' }}
              >
                {location.object
                  ? `${location.page}: ${location.object}`
                  : location.page}
              </Link>
            ) : (
              location.page
            ))}
        </Typography>
      </Stack>
    </Stack>
  )
}

/**
 * Header widget: who of the team is around (avatar stack) and, on click, what everybody is
 * looking at. Locations change with every navigation, so they are only followed live while
 * the list is open.
 */
const TeamPresence = () => {
  const { t } = useTranslation('presence')
  const team = usePresenceSelect.teamList()
  const watchLocations = usePresenceAction.watchLocations()
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)

  const isOpen = Boolean(anchorEl)

  React.useEffect(() => {
    watchLocations({ enabled: isOpen })
  }, [isOpen])

  React.useEffect(() => () => watchLocations({ enabled: false }), [])

  if (!team.length) return null

  const onlineCount = team.filter((member) => member.status === 'online').length

  return (
    <>
      <Tooltip title={t('team.onlineCount', { count: onlineCount })}>
        <ButtonBase
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ borderRadius: 6, px: 1 }}
          aria-label={t('team.title')}
        >
          {/* not MUI AvatarGroup: its overlap styles expect bare avatars, ours carry a badge */}
          <Stack direction="row" alignItems="center">
            {team.slice(0, MAX_AVATARS).map((member, index) => (
              <Box key={member.id} sx={{ ml: index ? -2 : 0, display: 'flex' }}>
                <UserStatusBadge status={member.status} size={7}>
                  <MemberAvatar member={member} size={28} />
                </UserStatusBadge>
              </Box>
            ))}
            {team.length > MAX_AVATARS && (
              <Avatar sx={{ ...getAvatarSx(28, false, 11), ml: -2 }}>
                +{team.length - MAX_AVATARS}
              </Avatar>
            )}
          </Stack>
        </ButtonBase>
      </Tooltip>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 320, mt: 2, p: 4 } }}
      >
        <Typography fontWeight={600} fontSize={14} color="grey.900" mb={1}>
          {t('team.title')}
        </Typography>

        <Stack sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {team.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              onNavigate={() => setAnchorEl(null)}
            />
          ))}
        </Stack>
      </Popover>
    </>
  )
}

export default TeamPresence
